/**
 * Sign-in pages post to their own address (ADR-0165). A page such as `/online/login` is shown
 * by GET and its form is sent by POST to the same address, as classic web forms do; the server
 * runs the API route named here (same handler, guards, rate limits and origin check). Web
 * filters that rate each address separately (Blue Coat WebPulse rated `/api/v1/auth/login`
 * "Suspicious" while the page itself opened) then see one address for the page and its form.
 *
 * Keys: page path under the app's base; values: the API route under `/api/v1`. Only requests
 * that carry no session: the session cookies are sent to `/api/v1` alone (`COOKIE_PATH`), so
 * signing out and everything after sign-in stay on the API.
 */
const MERCHANT_SIGN_IN = {
  login: '/auth/login',
  'login/select': '/auth/login/select',
  'forgot-password': '/auth/password/forgot',
  'reset-password': '/auth/password/reset',
} as const;

const INVITE = {
  'accept-invite': '/auth/invitations/accept',
  'accept-invite/preview': '/auth/invitations/preview',
} as const;

export const PAGE_ACTIONS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  manager: {
    ...MERCHANT_SIGN_IN,
    ...INVITE,
    register: '/auth/register',
  },
  pos: {
    ...MERCHANT_SIGN_IN,
    ...INVITE,
    'pin-sign-in': '/auth/pos/pin-sign-in',
    'pin-sign-in/branch': '/auth/pos/pin-sign-in/branch',
  },
  // The link-page editor and the online store console sign in with the manager session.
  links: MERCHANT_SIGN_IN,
  online: MERCHANT_SIGN_IN,
  platform: {
    login: '/auth/platform/login',
    'login/mfa': '/auth/platform/mfa',
    'forgot-password': '/auth/platform/password/forgot',
    'reset-password': '/auth/platform/password/reset',
    'accept-invitation/inspect': '/auth/platform/invitation/inspect',
    'accept-invitation/start': '/auth/platform/invitation/start',
    'accept-invitation/complete': '/auth/platform/invitation/complete',
  },
};

const own = (record: object, key: string) => Object.prototype.hasOwnProperty.call(record, key);

/**
 * The page address a POST to `apiPath` is sent to from the app at `pageBase` (`/online/`), or
 * null when that app has no such page (the request then goes to the API path).
 */
export function pageActionUrl(pageBase: string, apiPath: string): string | null {
  const app = pageBase.replace(/^\/+|\/+$/g, '');
  if (!own(PAGE_ACTIONS, app)) return null;
  const actions = PAGE_ACTIONS[app]!;
  const page = Object.keys(actions).find((key) => actions[key] === apiPath);
  return page ? `/${app}/${page}` : null;
}

/**
 * The API route (under `/api/v1`, query kept) a POST to a page address runs, or null when the
 * address is no sign-in page.
 */
export function pageActionTarget(url: string): string | null {
  const queryAt = url.indexOf('?');
  const path = queryAt < 0 ? url : url.slice(0, queryAt);
  const match = /^\/([a-z]+)\/([a-z]+(?:[-/][a-z]+)*)$/.exec(path);
  if (!match) return null;
  const [, app = '', page = ''] = match;
  if (!own(PAGE_ACTIONS, app)) return null;
  const actions = PAGE_ACTIONS[app]!;
  if (!own(actions, page)) return null;
  return `${actions[page]}${queryAt < 0 ? '' : url.slice(queryAt)}`;
}
